<template>
	<Teleport to="body">
		<Transition @after-leave="on_after_leave">
			<dialog v-if="is_open" ref="dialog_el" @cancel.prevent>
				<div
					ref="modal_el"
					:class="['modal', modal_class]"
					:style="width ? { maxWidth: `${width}px` } : null"
					role="dialog"
					aria-modal="true"
					:aria-labelledby="$slots.header ? header_id : undefined"
				>
					<button type="button" class="btn btn-close" @click="close">
						<v-icon slug="xmark" />
						<span class="screen-reader-text">Close modal</span>
					</button>

					<header v-if="$slots.header" :id="header_id" class="modal-header">
						<slot name="header" />
					</header>

					<div class="modal-body">
						<slot />
					</div>

					<footer v-if="$slots.footer" class="modal-footer">
						<slot name="footer" />
					</footer>
				</div>
			</dialog>
		</Transition>
	</Teleport>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

dialog {
	max-width: none;
	max-height: none;
	margin: auto;
	padding: 0;
	border: none;
	background: none;

	&::backdrop {
		background-color: rgba($black, 0.4);
	}

	&.v-enter-active,
	&.v-leave-active {
		transition: opacity 0.3s ease-in-out;

		.modal {
			transition: inherit;
		}
	}

	&.v-enter-from,
	&.v-leave-to {
		opacity: 0;
	}
}

.modal {
	position: relative;
	display: flex;
	flex-direction: column;
	width: calc(100vw - #{$spacer * 2});
	max-width: 32em;
	max-height: calc(100vh - #{$spacer * 2});
	margin: $spacer auto;
	overflow: hidden;
	border-radius: 1.25em;
	background-color: $white;

	.btn-close {
		position: absolute;
		top: 0.5em;
		right: 0.5em;
		z-index: 1;
		width: auto;
		height: auto;
		padding: 0.5em;
		border: none;
		background: none;
		cursor: pointer;

		svg {
			display: block;
			width: 1.25rem;
			height: 1.25rem;
		}
	}
}

.modal-header {
	flex: 0 0 auto;
	padding: 1em $spacer;
	background-color: $bg;
}

.modal-body {
	flex: 1 1 auto;
	overflow-y: auto;
	padding: 1em $spacer;
	min-height: 0;
}

.modal-footer {
	flex: 0 0 auto;
	padding: 1em $spacer;
	border-top: 1px solid $grey-border;
}
</style>

<script setup>
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { nextTick, onMounted, ref, useId } from 'vue';

import VIcon from './_v-icon.vue';

defineProps({
	modal_class: {
		type: String,
		required: false,
		default: ``,
	},
	width: {
		type: Number,
		required: false,
		default: 0,
	},
});

const emit = defineEmits([`closed`]);

const header_id = useId();

const is_open = ref(false);

const dialog_el = ref(null);

const modal_el = ref(null);

let previously_focused_el = null;

onClickOutside(modal_el, close);

onKeyStroke(`Escape`, () => {
	if (!is_open.value) {
		return;
	}

	close();
});

function open() {
	previously_focused_el = document.activeElement;

	is_open.value = true;

	nextTick(() => dialog_el.value?.showModal());
}

function close() {
	is_open.value = false;
}

function on_after_leave() {
	dialog_el.value?.close();

	previously_focused_el?.focus();

	emit(`closed`);
}

defineExpose({
	close,
});

onMounted(open);
</script>
